import type { Scale } from '@tunasong/schemas'
import type { Chord } from '../chord/index.js'
import { isChordNotes } from '../chord/chord-types.js'
import { getNoteChromaticDistance } from '../note/note-lib.js'
import { getDiatonicFunction } from './diatonic.js'
import type { ChordBorrowing, ChordFunction } from './types.js'

/**
 * If `chord` is a chord borrowed from the parallel mode of `scale`, return its
 * label and borrowing source. Otherwise return null.
 *
 * Strategy: build the parallel-mode scale (same root, opposite quality) and
 * check whether the chord is diatonic there. If so, re-use the diatonic label
 * the parallel scale produces (e.g., Eb in C major is the III of C minor →
 * labelled '♭III').
 *
 * Special case: ♭II (Phrygian / Neapolitan color) is not in the parallel
 * minor's natural diatonic set, so detect it explicitly: chord root is a
 * half-step above the tonic and chord is a plain major triad.
 */
export const getBorrowedFunction = (
  chord: Chord,
  scale: Scale
): { chordFunction: ChordFunction; borrowing: ChordBorrowing } | null => {
  // Skip if the chord is already diatonic — that's not borrowing.
  if (getDiatonicFunction(chord, scale)) {
    return null
  }

  // Build the parallel-mode scale.
  const parallelType = scale.type === 'major' ? 'minor' : 'major'
  const parallelScale: Scale = { ...scale, type: parallelType }

  const parallelDiatonic = getDiatonicFunction(chord, parallelScale)
  if (parallelDiatonic) {
    return { chordFunction: parallelDiatonic, borrowing: 'parallel-mode' }
  }

  // Phrygian / Neapolitan: ♭II in major.
  if (scale.type === 'major' && isPhrygianII(chord, scale)) {
    return { chordFunction: '♭II', borrowing: 'parallel-mode' }
  }

  return null
}

/**
 * Is `chord` the Phrygian ♭II of `scale`? — chord is a plain major triad whose
 * root is a half-step above the scale's tonic.
 *
 * Plain-major-triad in this codebase is represented as `variant: ''` (see
 * lib/music-lib/src/chord/chord-types.ts — the empty string is the canonical
 * major triad; 'M' is an alias produced by the parser in some paths). We
 * accept either, but reject anything that adds extensions, minor, dim, etc.
 *
 * `getInterval` from scale.ts only returns a degree for notes that are in the
 * scale, so it can't tell us the distance from C to Db (Db is outside C
 * major). We use `getNoteChromaticDistance` instead — it's defined as
 * (to - from + 12) % 12 and gives a clean 0–11 semitone count.
 */
const isPhrygianII = (chord: Chord, scale: Scale): boolean => {
  if (isChordNotes(chord)) {
    return false
  }
  if (chord.variant !== '' && chord.variant !== 'M') {
    return false
  }
  return getNoteChromaticDistance(scale.root, chord.root) === 1
}
